import React, { useState } from 'react';
import { Lock, Eye, EyeOff, User, Calendar, AlertTriangle, Loader2 } from 'lucide-react';

/**
 * Human-readable names for component types
 */
const componentTypeNames = {
  'commercial_flight': 'Flight',
  'private_flight': 'Private Flight',
  'hotel': 'Hotel',
  'rental_car': 'Car Rental',
  'car_rental': 'Car Rental',
  'restaurant': 'Restaurant',
  'meeting': 'Meeting',
  'event': 'Event',
  'chauffeur_service': 'Chauffeur Service',
  'memo': 'Memo',
  'general': 'General'
};

/**
 * Placeholder component for confidential items that haven't been revealed.
 * Shows minimal info: type, creator, confidential status.
 * Provides "Reveal" action that logs access.
 *
 * Used for both trip components and unfiled/inbox components.
 */
const ConfidentialPlaceholder = ({
  component,
  onReveal,
  onConceal,
  isRevealing = false,
  isConcealing = false,
  isRevealed = false, // If true, show "Hide" option instead of "Reveal"
  showConcealOption = true // Whether to show the conceal/hide option
}) => {
  const typeName = componentTypeNames[component.component_type] || 'Component';
  const creatorInfo = component.creator_info;
  const creatorName = creatorInfo?.name || 'Unknown User';

  // Format the start date/time if available
  const formatStartDateTime = () => {
    if (!component.start_datetime) return null;
    try {
      const date = new Date(component.start_datetime);
      return date.toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit'
      });
    } catch {
      return component.start_datetime;
    }
  };

  const startDateTime = formatStartDateTime();

  // Theme classes
  const containerClass = 'bg-ui-hover/90 border-ui-amber-line hover:border-ui-amber-solid/60';

  const textPrimaryClass = 'text-ui-amber-fg';
  const textSecondaryClass = 'text-ui-ink-3';
  const textMutedClass = 'text-ui-muted';
  const iconBgClass = 'bg-ui-amber-bg';
  const warningBgClass = 'bg-ui-line';
  const warningTextClass = 'text-ui-ink-3';

  const buttonClass = 'bg-ui-amber-bg text-ui-amber-fg border-ui-amber-line hover:bg-ui-amber-line';

  return (
    <div className={`rounded-xl border-2 border-dashed p-4 sm:p-6 transition-all ${containerClass}`}>
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        {/* Left side: Icon and info */}
        <div className="flex items-start gap-3">
          <div className={`p-2.5 sm:p-3 rounded-lg ${iconBgClass} flex-shrink-0`}>
            <Lock className={`w-5 h-5 sm:w-6 sm:h-6 ${textPrimaryClass}`} />
          </div>
          <div className="min-w-0 flex-1">
            <div className={`text-sm font-semibold ${textPrimaryClass}`}>
              Confidential {typeName}
            </div>
            <div className={`text-xs mt-1 flex items-center ${textSecondaryClass}`}>
              <User className="w-3 h-3 mr-1 flex-shrink-0" />
              <span className="truncate">Created by {creatorName}</span>
            </div>
            {startDateTime && (
              <div className={`text-xs mt-1 flex items-center ${textMutedClass}`}>
                <Calendar className="w-3 h-3 mr-1 flex-shrink-0" />
                <span className="truncate">{startDateTime}</span>
              </div>
            )}
          </div>
        </div>

        {/* Right side: Action button */}
        <div className="flex-shrink-0 self-start sm:self-center">
          {!isRevealed ? (
            <button
              onClick={() => onReveal && onReveal(component.id)}
              disabled={isRevealing}
              className={`inline-flex items-center px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg text-xs sm:text-sm font-medium border transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${buttonClass}`}
            >
              {isRevealing ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1.5 animate-spin" />
                  Revealing...
                </>
              ) : (
                <>
                  <Eye className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1.5" />
                  Reveal Content
                </>
              )}
            </button>
          ) : showConcealOption && (
            <button
              onClick={() => onConceal && onConceal(component.id)}
              disabled={isConcealing}
              className={`inline-flex items-center px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg text-xs sm:text-sm font-medium border transition-colors disabled:opacity-50 disabled:cursor-not-allowed bg-ui-line text-ui-ink-2 border-ui-control hover:bg-ui-control`}
            >
              {isConcealing ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1.5 animate-spin" />
                  Hiding...
                </>
              ) : (
                <>
                  <EyeOff className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1.5" />
                  Hide Again
                </>
              )}
            </button>
          )}
        </div>
      </div>

      {/* Warning message */}
      <div className={`mt-4 p-3 rounded-lg ${warningBgClass}`}>
        <div className={`flex items-start gap-2 text-xs ${warningTextClass}`}>
          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>
            {isRevealed
              ? 'You have revealed this confidential content. Your access was logged. You can hide it again to restore the placeholder view.'
              : 'This content is marked as confidential. Revealing it will log your access to the organization audit log.'
            }
          </span>
        </div>
      </div>
    </div>
  );
};

export default ConfidentialPlaceholder;
